/**
 * Morse — die Tagesaufnahme hält für die Hörspur kurz an. (07.10.2026)
 *
 * Martin, 10:51 mit Bild: „Der Button geht nicht.“ Oben lief die Tagesaufnahme seit einer Stunde (1:02:05), und die
 * Hörspur verweigerte jeden Start (`aufnahmeLaeuft()` = Sprechtaste ODER Tagesaufnahme) — der Grund stand nur als
 * blasse Zeile unter dem Knopf. Die Tagesaufnahme läuft oft stundenlang; so war die Vorführung praktisch nie zu hören.
 *
 * Jetzt geht die Hörspur den Weg, den die Sprechtaste seit Juli geht: die Tagesaufnahme WEICH anhalten
 * (`pauseDayRecorderForMic`, eigener Grund „morse“ in der Gründe-Menge von native-recorder.ts), spielen, danach
 * fortsetzen (`resumeDayRecorderAfterMic("morse")`). Die Menge sorgt dafür, dass erst fortgesetzt wird, wenn niemand
 * sonst (Sprechtaste, Vorlesen, Diktat, Anruf) die Aufnahme noch angehalten haben will. Die Sprechtaste selbst bleibt
 * ein hartes „die Hörspur schweigt“ (morse-hoerspur.ts) — sie nimmt in der Seite auf.
 *
 * Warum anhalten statt darüberspielen: die Hörspur weckt den Kontext und stellt die Tonsitzung für die ganze Spur auf
 * `playback` (morse-hoerspur.ts, `halteTon`). Mit einer angehaltenen Tagesaufnahme ist das derselbe Zustand wie beim
 * Vorlesen (ton-rekorder-schonung.ts hält sie dafür ebenso an), und die Töne landen nicht in der Aufnahme.
 *
 * Regeln — jede ist ein Endfall der Hörspur:
 *   - EIN Halt, EIN Fortsetzen. Jedes Ende (`lasse`) setzt genau einmal fort — auch wenn es kommt, während das Anhalten
 *     noch unterwegs ist (dann gleich nach dessen Antwort). Ein `halte` im Nachlauf fragt das Anhalten NOCH EINMAL an:
 *     in den 3 s kann die Aufnahme von außen weitergelaufen sein (Knopf „Fortsetzen“) — sonst spielte die Spur über
 *     eine laufende Aufnahme, und die Karte behauptete das Gegenteil. Das ist gefahrlos: „morse“ steht noch als Grund
 *     in der Menge, `pauseDayRecorderForMic` trägt dann nur ein (oder hält wirklich neu an, wenn sie wieder aufnimmt).
 *   - Läuft keine Tagesaufnahme, geschieht nichts: kein Anhalten, kein Fortsetzen.
 *   - Fortgesetzt wird nach `HALT_NACHLAUF_MS` (der Kontext schläft 1,5 s nach dem letzten Ton, die Tonsitzung kehrt
 *     nach 1,5 s zurück — erst dann bekommt der Rekorder das Mikrofon sauber zurück; ein „Weiter“ in dieser Zeit hält
 *     einfach weiter an, statt die Aufnahme kurz anlaufen zu lassen). SOFORT, wenn die Seite geht (Hintergrund,
 *     `pagehide`, zerstört): dann läuft womöglich keine Uhr mehr.
 *   - Notbremse: dauert ein Halt länger als die Spur plus `HALT_PUFFER_MS`, wird fortgesetzt, und die Hörspur hält an.
 *     Gezählt ab dem ERSTEN Anhalten und nie länger als `HALT_HOECHSTENS_MS` — auch wenn ein Kapitelsprung zurück die
 *     Spur verlängert: so läuft sie immer VOR der 5-Minuten-Uhr von native-recorder.ts ab (die räumt ALLE Gründe).
 *   - Kommt die Antwort auf das Anhalten nicht binnen `HALT_GREIFT_HOECHSTENS_MS` (Brücke hängt), gilt der Griff als
 *     verloren: der Grund wird abgemeldet (Fortsetzen) — kommt die Antwort doch noch, wird gleich danach fortgesetzt.
 *   - Alle Aufrufe ans Native laufen nacheinander (eine Kette): ein Anhalten wartet, bis ein Fortsetzen durch ist.
 *   - Nach einem Neuladen der Seite ist dieser Halt vergessen. Beim `pagehide` stößt die Seite das Fortsetzen noch an —
 *     es braucht aber drei Rundläufe über die Brücke (Abgleich, Freiräumen, `resume()`), und die Seite ist oft schon
 *     weg, bevor sie fertig sind. Danach setzen die Pause-Wache in native-recorder.ts (`startDayRecorderPauseWatch`:
 *     4 s nach dem Start, dann 20 s) und aufnahme-wache.ts eine liegengebliebene Pause NUR fort, wenn die Tages-Absicht
 *     oder „Durchgehend aufnehmen“ gesetzt ist — sonst bleibt sie bis zum nächsten Griff an den Knopf stehen (dieselbe
 *     Altlast wie beim Vorlesen, ton-rekorder-schonung.ts). Martins Tagesaufnahme läuft mit der Tages-Absicht.
 *
 * Diagnose: Zeilen „[morse-spur] tagesaufnahme=pause|fortsetzen|aus …“ (Liste, Anlass, Dauer — nie Inhalt).
 */
import { tagesaufnahmeLaeuft } from "./playback-hifi.js";

/** Der Grund in der Gründe-Menge von native-recorder.ts (steht so auch in deren Protokollzeilen). */
export const MORSE_HALT_GRUND = "morse";
/** So lange nach dem Ende (oder der Pause) der Hörspur wird fortgesetzt — nach Schlaf des Kontexts und der Tonsitzung. */
export const HALT_NACHLAUF_MS = 3000;
/** So lange wartet der erste Ton höchstens auf das Anhalten — dann spielt die Hörspur nicht und sagt es. */
export const HALT_FRIST_MS = 2500;
/** Die Notbremse: Dauer der Spur plus so viel … */
export const HALT_PUFFER_MS = 60_000;
/** … aber nie länger als das (die Notbremse von native-recorder.ts greift nach 5 Minuten und räumt ALLE Gründe). */
export const HALT_HOECHSTENS_MS = 270_000;
/** Kommt die Antwort auf das Anhalten nicht binnen dieser Zeit, gilt der Griff als verloren. */
export const HALT_GREIFT_HOECHSTENS_MS = 20_000;

/** frei: nichts gehalten · greift: das Anhalten ist unterwegs · haelt: angehalten · nachlauf: das Fortsetzen ist geplant. */
type HaltZustand = "frei" | "greift" | "haelt" | "nachlauf";
/**
 * Die Antwort des Rekorders auf „anhalten“: angehalten · es nahm nichts auf · er nimmt weiter auf, weil er keine Pause
 * kennt (Browser-Rekorder) · er nimmt weiter auf, obwohl er eine kennt (Fehler — gleich noch einmal versuchen).
 */
export type PausenAntwort = "angehalten" | "aus" | "nimmt-auf" | "fehler";
/** Was die Hörspur erfährt: angehalten / nichts zu tun / ließ sich nicht anhalten / keine Antwort in der Frist. */
export type HaltErgebnis = PausenAntwort | "frist";

/** Der Anschluss an die Tagesaufnahme — in der App native-recorder.ts, im Test eine Attrappe. */
export interface TagesaufnahmeAnschluss {
  /** Läuft eine Tagesaufnahme (nimmt auf, ist angehalten, schließt ab, oder die Tages-Absicht steht)? Nein: nichts tun. */
  laeuft: () => boolean;
  /** Anhalten (mit dem Grund). */
  pausiere: (grund: string) => Promise<PausenAntwort>;
  /** Den Grund abmelden — fortgesetzt wird, wenn keiner mehr gilt (native-recorder.ts). */
  setzeFort: (grund: string) => Promise<void>;
}

/** Was die Hörspur vom Halt braucht. */
export interface SpurHalt {
  halte(kennung: string, spurMs: number): Promise<HaltErgebnis>;
  lasse(kennung: string, o?: { sofort?: boolean }): void;
  /** Für die Anzeige: ist die Tagesaufnahme gerade für die Hörspur angehalten (oder wird es gleich)? */
  readonly angehalten: boolean;
  /** Der Griff ist unterwegs — die Abfrage davor meldet noch „recording“ (morse-view.ts hört darauf). */
  readonly greift: boolean;
  verbinde(o: { beiWechsel?: () => void; beiNotbremse?: () => void }): void;
}

/** Der Anschluss der App: der Merker der Tonanlage (Bringschuld von native-recorder.ts) und native-recorder.ts selbst. */
export function tagesaufnahmeAusApp(): TagesaufnahmeAnschluss {
  return {
    laeuft: () => tagesaufnahmeLaeuft(),
    pausiere: async (grund) => {
      const m = await import("./native-recorder.js");
      if (await m.pauseDayRecorderForMic(grund)) return "angehalten";
      // Nicht angehalten: nimmt sie im Browser auf (der Browser-Rekorder kennt keine Pause, halteJetztAn)? Nimmt sie in
      // der App weiter auf (eine Ausnahme beim Anhalten)? Oder nahm sie gar nicht auf (aus, schon pausiert, zwischen
      // zwei Sitzungen)?
      if (m.laeuftImBrowser()) return "nimmt-auf";
      return m.getRecorderState() === "recording" ? "fehler" : "aus";
    },
    setzeFort: async (grund) => {
      const m = await import("./native-recorder.js");
      await m.resumeDayRecorderAfterMic(grund);
    },
  };
}

interface HaltOptionen {
  anschluss: TagesaufnahmeAnschluss;
  /** Diagnosezeile (ohne „[morse-spur] “). */
  spur: (text: string) => void;
  /** Zeit in ms (für die Dauer in der Diagnose). */
  jetzt: () => number;
}

export class TagesaufnahmeHalt implements SpurHalt {
  private _zustand: HaltZustand = "frei";
  /** Die Antwort auf das laufende Anhalten (für einen zweiten `halte` in dieser Zeit — kein zweiter Griff). */
  private griff: Promise<PausenAntwort> | null = null;
  /** `lasse` kam, während das Anhalten unterwegs war: nach der Antwort fortsetzen. */
  private losNach: { kennung: string; sofort: boolean } | null = null;
  private kennung = "";
  /** Wann die Notbremse spätestens fortsetzt (Uhr von `jetzt`). */
  private notBis = 0;
  /** Seit wann gehalten wird — ab dem ERSTEN Anhalten, über ein „Weiter“ im Nachlauf hinweg. */
  private seit = 0;
  /** Steht „morse“ gerade in der Gründe-Menge von native-recorder.ts (nach „angehalten“, bis zum Fortsetzen)? */
  private eingetragen = false;
  private nachlaufUhr: ReturnType<typeof setTimeout> | null = null;
  private notUhr: ReturnType<typeof setTimeout> | null = null;
  private greiftUhr: ReturnType<typeof setTimeout> | null = null;
  private kette: Promise<unknown> = Promise.resolve();
  private beiWechsel: () => void = () => undefined;
  private beiNotbremse: () => void = () => undefined;

  constructor(private readonly o: HaltOptionen) {}

  get zustand(): HaltZustand {
    return this._zustand;
  }
  get angehalten(): boolean {
    return this._zustand === "greift" || this._zustand === "haelt";
  }
  get greift(): boolean {
    return this._zustand === "greift";
  }

  verbinde(o: { beiWechsel?: () => void; beiNotbremse?: () => void }): void {
    if (o.beiWechsel) this.beiWechsel = o.beiWechsel;
    if (o.beiNotbremse) this.beiNotbremse = o.beiNotbremse;
  }

  /**
   * Vor dem ersten Ton (Start, Weiter, Kapitelsprung): anhalten, falls eine Tagesaufnahme läuft. Hält es schon an,
   * gilt nur die Notbremse neu (und ein geplantes Fortsetzen entfällt). `kennung`: „liste=demo anlass=start“.
   * `spurMs`: wie lange die Spur höchstens noch dauert (für die Notbremse).
   */
  halte(kennung: string, spurMs: number): Promise<HaltErgebnis> {
    const jetzt = this.o.jetzt();
    const spur = jetzt + Math.max(0, spurMs) + HALT_PUFFER_MS;
    if (this._zustand === "haelt") {
      // Kapitelsprung beim Spielen: die Spur dauert vielleicht länger — aber nie über die Grenze ab dem ersten Anhalten.
      this.notBis = Math.min(spur, this.seit + HALT_HOECHSTENS_MS);
      this.stelleNotbremse();
      return Promise.resolve("angehalten");
    }
    if (this._zustand === "greift" && this.griff) {
      // Ein zweiter Start in derselben Zeit (Doppeltipp, Weiter): kein zweiter Griff — und ein Ende davor gilt nicht mehr.
      this.losNach = null;
      return this.mitFrist(this.griff);
    }
    if (this._zustand === "nachlauf") {
      // „Weiter“ oder ein neuer Start im Nachlauf: das geplante Fortsetzen entfällt, das Anhalten wird neu angefragt —
      // die Aufnahme kann inzwischen von außen weitergelaufen sein. Die Grenze zählt weiter ab dem ersten Anhalten.
      this.uhrAus("nachlauf");
      this.notBis = Math.min(spur, this.seit + HALT_HOECHSTENS_MS);
      return this.greife(kennung, "pause-erneut");
    }
    if (!this.laeuftEine()) {
      this.o.spur(`tagesaufnahme=aus ${kennung}`);
      return Promise.resolve("aus");
    }
    this.seit = jetzt;
    this.notBis = Math.min(spur, jetzt + HALT_HOECHSTENS_MS);
    return this.greife(kennung, "pause");
  }

  /** Das Anhalten anfragen (über die Kette). `art`: „pause“ (erstes) oder „pause-erneut“ (aus dem Nachlauf). */
  private greife(kennung: string, art: "pause" | "pause-erneut"): Promise<HaltErgebnis> {
    this._zustand = "greift";
    this.kennung = kennung;
    this.losNach = null;
    this.o.spur(`tagesaufnahme=${art} ${kennung}`);
    const griff = this.reihe(() => this.o.anschluss.pausiere(MORSE_HALT_GRUND)).then(
      (a) => a,
      // Ohne Antwort weiß niemand, ob sie anhielt: lieber nicht über eine laufende Aufnahme spielen.
      (): PausenAntwort => "fehler",
    );
    this.griff = griff;
    void griff.then((a) => this.nachGriff(griff, a));
    this.greiftUhr = setTimeout(() => this.griffVerloren(griff), HALT_GREIFT_HOECHSTENS_MS);
    this.wechsel();
    return this.mitFrist(griff);
  }

  /**
   * Die Hörspur endet oder pausiert: fortsetzen — nach dem Nachlauf, oder `sofort` (die Seite geht). Ist das Anhalten
   * noch unterwegs, gleich nach dessen Antwort. Ohne Halt: nichts.
   */
  lasse(kennung: string, o: { sofort?: boolean } = {}): void {
    const sofort = o.sofort === true;
    switch (this._zustand) {
      case "frei":
        return;
      case "greift":
        this.losNach = { kennung, sofort };
        return;
      case "haelt":
        if (sofort) return this.fortsetzen(kennung);
        this._zustand = "nachlauf";
        this.nachlaufUhr = setTimeout(() => {
          this.nachlaufUhr = null;
          this.fortsetzen(kennung);
        }, HALT_NACHLAUF_MS);
        this.wechsel();
        return;
      case "nachlauf":
        if (sofort) this.fortsetzen(kennung);
        return;
    }
  }

  /* ── intern ──────────────────────────────────────────────────────── */

  /** Läuft eine Tagesaufnahme? Wirft die Abfrage, gilt: nein (dann wird nichts angehalten und nichts fortgesetzt). */
  private laeuftEine(): boolean {
    try {
      return this.o.anschluss.laeuft();
    } catch {
      return false;
    }
  }

  private mitFrist(griff: Promise<PausenAntwort>): Promise<HaltErgebnis> {
    let uhr: ReturnType<typeof setTimeout> | undefined;
    const frist = new Promise<HaltErgebnis>((fertig) => {
      uhr = setTimeout(() => fertig("frist"), HALT_FRIST_MS);
    });
    return Promise.race([griff, frist]).finally(() => clearTimeout(uhr));
  }

  /** Die Antwort auf das Anhalten. */
  private nachGriff(griff: Promise<PausenAntwort>, antwort: PausenAntwort): void {
    if (this.griff !== griff) {
      // Der Griff galt schon als verloren (griffVerloren) — der Grund ist abgemeldet. Hielt sie doch noch an, wird
      // jetzt fortgesetzt: sonst stünde eine Aufnahme ohne Grund bis zur Pause-Wache (3 min).
      if (antwort === "angehalten") {
        this.o.spur(`tagesaufnahme=fortsetzen ${this.kennung} anlass=spaete-antwort`);
        void this.reihe(() => this.o.anschluss.setzeFort(MORSE_HALT_GRUND)).catch(() => undefined);
      }
      return;
    }
    this.griff = null;
    this.uhrAus("greift");
    const los = this.losNach;
    this.losNach = null;
    if (antwort === "angehalten") {
      this._zustand = "haelt";
      this.eingetragen = true;
      this.stelleNotbremse();
      this.wechsel();
      if (los) this.lasse(los.kennung, { sofort: los.sofort });
      return;
    }
    this._zustand = "frei";
    const grund = antwort === "nimmt-auf" ? "nimmt-weiter-auf" : antwort === "fehler" ? "fehler" : "nichts-angehalten";
    this.o.spur(`tagesaufnahme=aus ${this.kennung} grund=${grund}`);
    this.wechsel();
    if (this.eingetragen) {
      // Ein erneutes Anhalten (aus dem Nachlauf) ging daneben, „morse“ steht aber noch in der Menge: abmelden.
      this.eingetragen = false;
      this.o.spur(`tagesaufnahme=fortsetzen ${this.kennung} anlass=abmelden`);
      void this.reihe(() => this.o.anschluss.setzeFort(MORSE_HALT_GRUND)).catch(() => undefined);
    }
  }

  /** Die Brücke antwortet nicht: den Grund abmelden, damit keine Aufnahme an ihm hängen bleibt. */
  private griffVerloren(griff: Promise<PausenAntwort>): void {
    this.greiftUhr = null;
    if (this.griff !== griff || this._zustand !== "greift") return;
    this.griff = null;
    this.losNach = null;
    this._zustand = "frei";
    this.eingetragen = false;
    this.o.spur(`tagesaufnahme=fortsetzen ${this.kennung} anlass=keine-antwort`);
    this.wechsel();
    // Nicht in die Kette: die hängt hinter genau diesem Griff. Das Abmelden des Grunds geschieht dort sofort.
    void this.o.anschluss.setzeFort(MORSE_HALT_GRUND).catch(() => undefined);
  }

  /** Genau einmal fortsetzen — nur, was von hier angehalten wurde. */
  private fortsetzen(kennung: string): void {
    if (this._zustand !== "haelt" && this._zustand !== "nachlauf") return;
    this.uhrAus("nachlauf");
    this.uhrAus("not");
    this._zustand = "frei";
    this.eingetragen = false;
    const sek = Math.max(0, Math.round((this.o.jetzt() - this.seit) / 1000));
    this.o.spur(`tagesaufnahme=fortsetzen ${kennung} gehalten=${sek}s`);
    this.wechsel();
    void this.reihe(() => this.o.anschluss.setzeFort(MORSE_HALT_GRUND)).catch(() => undefined);
  }

  private stelleNotbremse(): void {
    this.uhrAus("not");
    this.notUhr = setTimeout(() => {
      this.notUhr = null;
      if (this._zustand !== "haelt" && this._zustand !== "nachlauf") return;
      this.fortsetzen(`${this.kennung} anlass=notbremse`);
      try {
        this.beiNotbremse();
      } catch {
        /* die Aufnahme läuft wieder — das ist das Wichtige */
      }
    }, Math.max(0, this.notBis - this.o.jetzt()));
  }

  private uhrAus(welche: "nachlauf" | "not" | "greift"): void {
    const uhr = welche === "nachlauf" ? this.nachlaufUhr : welche === "not" ? this.notUhr : this.greiftUhr;
    if (uhr) clearTimeout(uhr);
    if (welche === "nachlauf") this.nachlaufUhr = null;
    else if (welche === "not") this.notUhr = null;
    else this.greiftUhr = null;
  }

  /** Nacheinander ans Native — ein Anhalten überholt nie ein Fortsetzen (und umgekehrt). */
  private reihe<T>(fn: () => Promise<T>): Promise<T> {
    const p = this.kette.then(fn);
    this.kette = p.catch(() => undefined);
    return p;
  }

  private wechsel(): void {
    try {
      this.beiWechsel();
    } catch {
      /* eine Anzeige ist nie wichtiger als die Aufnahme */
    }
  }
}
